import { withTextureCopy } from "thimbleberry";
import { withErrorScopes } from "./ErrorScopes.ts";
import { renderFrame } from "./FragmentRender.ts";

export interface SimpleRenderParams {
  device: GPUDevice;

  /** shader module to run, presumed to have one vertex and one fragment entry */
  module: GPUShaderModule;

  /** format of the output texture. default "rgba32float" */
  outputFormat?: GPUTextureFormat;

  /** size of the output texture. default [1, 1]
   * Use [2, 2] for derivative tests
   * Use [512, 512] for visual image tests */
  size?: [number, number];

  /** Input textures to bind in group 0.
   * Bindings: textures at [1..n], samplers at [n+1..n+m]. */
  textures?: GPUTexture[];

  /** Samplers for the input textures.
   * Must be length 1 (reused for all textures) or match textures.length exactly. */
  samplers?: GPUSampler[];

  /** pass these uniforms to the shader in group 0 binding 0 */
  uniformBuffer?: GPUBuffer;

  /** Pre-built bind group (skips internal createBindGroup) */
  bindGroup?: GPUBindGroup;

  /** Layout for pre-built bind group */
  bindGroupLayout?: GPUBindGroupLayout;

  /** Extra bind-group entries to merge with the standard uniform/texture/sampler layout. */
  extraEntries?: GPUBindGroupEntry[];

  /** Matching extra layout entries for {@link extraEntries}. */
  extraLayoutEntries?: GPUBindGroupLayoutEntry[];

  /** Fragment entry point name. Required when the module has more than one @fragment. */
  fragmentEntryPoint?: string;

  /** Vertex entry point name. Required when the module has more than one @vertex. */
  vertexEntryPoint?: string;
}

/**
 * Executes a render pipeline with the given shader module.
 * Creates a texture with the specified format, renders to it, and returns all pixel data.
 * @returns output texture contents in a flattened array (rows flattened, color channels interleaved).
 */
export async function simpleRender(
  params: SimpleRenderParams,
): Promise<number[]> {
  const { device, module } = params;
  const { outputFormat = "rgba32float", size = [1, 1] } = params;
  const { textures = [], samplers = [], uniformBuffer } = params;

  return await withErrorScopes(device, async () => {
    const texture = device.createTexture({
      label: "fragment-test-output",
      size: { width: size[0], height: size[1], depthOrArrayLayers: 1 },
      format: outputFormat,
      usage: GPUTextureUsage.RENDER_ATTACHMENT | GPUTextureUsage.COPY_SRC,
    });
    const bindings = resolveBindings(
      params,
      device,
      uniformBuffer,
      textures,
      samplers,
    );
    const pipelineLayout = bindings
      ? device.createPipelineLayout({ bindGroupLayouts: [bindings.layout] })
      : "auto";
    const pipeline = device.createRenderPipeline({
      layout: pipelineLayout,
      vertex: { module, entryPoint: params.vertexEntryPoint },
      fragment: {
        module,
        entryPoint: params.fragmentEntryPoint,
        targets: [{ format: outputFormat }],
      },
      primitive: { topology: "triangle-list" },
    });
    renderFrame({
      device,
      pipeline,
      bindGroup: bindings?.bindGroup,
      targetView: texture.createView(),
    });
    const data = await withTextureCopy(device, texture, texData =>
      Array.from(texData),
    );
    texture.destroy();
    uniformBuffer?.destroy();
    return data;
  });
}

/**
 * Create bind group with optional uniforms, textures, and samplers.
 * Binding layout: 0=uniform buffer (if provided), 1..n=textures, n+1..n+m=samplers.
 * Samplers must be length 1 (reused for all textures) or match textures.length exactly.
 */
export function createBindGroup(
  device: GPUDevice,
  uniformBuffer: GPUBuffer | undefined,
  textures: GPUTexture[] = [],
  samplers: GPUSampler[] = [],
  extra?: {
    entries?: GPUBindGroupEntry[];
    layoutEntries?: GPUBindGroupLayoutEntry[];
  },
): { layout: GPUBindGroupLayout; bindGroup: GPUBindGroup } {
  if (textures.length > 0 && samplers.length > 0) {
    if (samplers.length !== 1 && samplers.length !== textures.length) {
      throw new Error(
        `Invalid sampler count: expected 1 or ${textures.length} samplers for ${textures.length} textures, got ${samplers.length}`,
      );
    }
  }

  const vis = GPUShaderStage.FRAGMENT;
  const layoutEntries: GPUBindGroupLayoutEntry[] = [];
  const bgEntries: GPUBindGroupEntry[] = [];

  if (uniformBuffer) {
    layoutEntries.push({
      binding: 0,
      visibility: vis,
      buffer: { type: "uniform" },
    });
    bgEntries.push({ binding: 0, resource: { buffer: uniformBuffer } });
  }

  textures.forEach((texture, i) => {
    const binding = i + 1;
    layoutEntries.push({
      binding,
      visibility: vis,
      texture: { sampleType: "float" },
    });
    bgEntries.push({ binding, resource: texture.createView() });
  });

  const singleSampler = samplers.length === 1 ? samplers[0] : undefined;
  for (let i = 0; i < textures.length; i++) {
    const binding = textures.length + i + 1;
    const sampler = singleSampler ?? samplers[i];
    layoutEntries.push({
      binding,
      visibility: vis,
      sampler: { type: "filtering" },
    });
    bgEntries.push({ binding, resource: sampler });
  }

  if (extra?.layoutEntries) layoutEntries.push(...extra.layoutEntries);
  if (extra?.entries) bgEntries.push(...extra.entries);

  const layout = device.createBindGroupLayout({ entries: layoutEntries });
  const bindGroup = device.createBindGroup({ layout, entries: bgEntries });
  return { layout, bindGroup };
}

type BindGroupAndLayout = {
  bindGroup: GPUBindGroup;
  layout: GPUBindGroupLayout;
};

/** Resolve bind group from pre-built params or by creating one from resources. */
function resolveBindings(
  params: SimpleRenderParams,
  device: GPUDevice,
  uniformBuffer: GPUBuffer | undefined,
  textures: GPUTexture[],
  samplers: GPUSampler[],
): BindGroupAndLayout | undefined {
  if (params.bindGroup) {
    return { bindGroup: params.bindGroup, layout: params.bindGroupLayout! };
  }
  const hasExtras =
    !!params.extraEntries?.length || !!params.extraLayoutEntries?.length;
  if (uniformBuffer || textures.length > 0 || hasExtras) {
    return createBindGroup(device, uniformBuffer, textures, samplers, {
      entries: params.extraEntries,
      layoutEntries: params.extraLayoutEntries,
    });
  }
}
